import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';

const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
// GIS only honours widths in this range and clamps silently outside it
const GSI_MIN_WIDTH = 200;
const GSI_MAX_WIDTH = 400;
const NO_TEARDOWN = () => {};

export interface GoogleButtonOptions {
  text: 'signin_with' | 'signup_with' | 'continue_with';
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAuthService {
  private platformId = inject(PLATFORM_ID);
  private scriptPromise: Promise<void> | null = null;

  constructor(
    private zone: NgZone,
    private logger: LoggerService,
  ) {}

  /**
   * Loads the GSI client script and renders the official "Sign in with Google"
   * button into the given container. onCredential receives the Google ID token
   * and is executed inside the Angular zone.
   */
  async renderButton(
    container: HTMLElement,
    options: GoogleButtonOptions,
    onCredential: (credential: string) => void,
  ): Promise<() => void> {
    if (!isPlatformBrowser(this.platformId)) {
      return NO_TEARDOWN;
    }
    if (!environment.googleClientId) {
      throw new Error('googleClientId is not configured for this environment');
    }

    await this.loadScript();

    const google = window.google;
    if (!google) {
      throw new Error('Google Identity Services failed to initialize');
    }

    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      // The GIS callback fires outside Angular's zone — re-enter it or the UI won't update
      callback: (response) => this.zone.run(() => onCredential(response.credential)),
      use_fedcm_for_prompt: true,
    });

    this.drawButton(container, options);
    const stopObserving = this.redrawOnResize(container, options);

    this.logger.log('🔑 [GoogleAuthService] Google button rendered');
    return stopObserving;
  }

  private drawButton(container: HTMLElement, options: GoogleButtonOptions): void {
    // GIS appends to the container, so a redraw would stack a second button
    container.replaceChildren();
    window.google?.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: options.text,
      shape: 'rectangular',
      logo_alignment: 'center',
      locale: 'de',
      width: this.buttonWidth(container),
    });
  }

  private buttonWidth(container: HTMLElement): number {
    const measured = container.offsetWidth || GSI_MAX_WIDTH;
    return Math.min(Math.max(measured, GSI_MIN_WIDTH), GSI_MAX_WIDTH);
  }

  /**
   * Callers render the button while its container is still hidden, so it first
   * measures 0 wide. GIS sizes the button once and never reflows it, so redraw
   * it once the real width arrives -- and on later viewport changes.
   */
  private redrawOnResize(container: HTMLElement, options: GoogleButtonOptions): () => void {
    if (typeof ResizeObserver === 'undefined') {
      return NO_TEARDOWN;
    }
    // The raw measurement, not the clamped width: a hidden container reads 0,
    // which must stay distinguishable from a real 400px-wide one
    let lastMeasured = container.offsetWidth;
    const observer = new ResizeObserver(() => {
      const measured = container.offsetWidth;
      // Hidden again: leave the button as it is and wait for the next reveal
      if (measured === 0 || measured === lastMeasured) {
        return;
      }
      lastMeasured = measured;
      this.drawButton(container, options);
    });
    // Outside the zone: a resize never changes Angular-bound state
    this.zone.runOutsideAngular(() => observer.observe(container));
    return () => observer.disconnect();
  }

  private loadScript(): Promise<void> {
    if (this.scriptPromise) {
      return this.scriptPromise;
    }
    this.scriptPromise = new Promise<void>((resolve, reject) => {
      if (window.google?.accounts) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = GSI_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Allow a retry on the next render attempt (e.g. after an adblocker prompt)
        this.scriptPromise = null;
        reject(new Error('Failed to load Google Identity Services script'));
      };
      document.head.appendChild(script);
    });
    return this.scriptPromise;
  }
}
